import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { switchLangUrl, normalizeI18nLang, SupportedLang } from '../../utils/lang';
import './LanguageSelector.css';

interface Language {
  code: SupportedLang;
  name: string;
  region: string;
}

/**
 * Inline SVG flags — Unicode flag emoji (🇺🇸🇹🇼🇯🇵) is unreliable:
 * Windows often renders regional-indicator pairs as blank or as
 * two-letter country codes that then vanish under Inter / CSS filters.
 */
function FlagSvg({ lang }: { lang: SupportedLang }): React.ReactElement {
  if (lang === 'en') {
    return (
      <svg viewBox="0 0 19 10" aria-hidden="true" focusable="false">
        <rect width="19" height="10" fill="#B22234" />
        <path
          d="M0 1.54h19M0 3.08h19M0 4.62h19M0 6.15h19M0 7.69h19M0 9.23h19"
          stroke="#fff"
          strokeWidth="0.77"
        />
        <rect width="7.6" height="5.38" fill="#3C3B6E" />
      </svg>
    );
  }

  if (lang === 'zh') {
    const rays = Array.from({ length: 12 }, (_, i) => (
      <rect
        key={i}
        x="-1.2"
        y="-12"
        width="2.4"
        height="24"
        transform={`rotate(${i * 15})`}
        fill="#fff"
      />
    ));
    return (
      <svg viewBox="0 0 90 60" aria-hidden="true" focusable="false">
        <rect width="90" height="60" fill="#FE0000" />
        <rect width="45" height="30" fill="#000095" />
        <g transform="translate(22.5 15)">
          {rays}
          <circle r="6" fill="#000095" />
          <circle r="5" fill="#fff" />
        </g>
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 90 60" aria-hidden="true" focusable="false">
      <rect width="90" height="60" fill="#fff" />
      <circle cx="45" cy="30" r="18" fill="#BC002D" />
    </svg>
  );
}

const LanguageSelector: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const languages: Language[] = [
    { code: 'en', name: 'English', region: 'US' },
    { code: 'zh', name: '中文', region: 'TW' },
    { code: 'ja', name: '日本語', region: 'JP' },
  ];

  const currentCode = normalizeI18nLang(i18n.language);
  const currentLanguage = languages.find((lang) => lang.code === currentCode) || languages[0];

  const handleLanguageChange = (languageCode: SupportedLang): void => {
    setIsOpen(false);
    if (languageCode === currentCode) return;

    // Persist the user's explicit choice so subsequent visits to "/" honour it.
    try {
      window.localStorage.setItem('i18nextLng', languageCode);
    } catch {
      // localStorage may be disabled (private mode) — ignore.
    }

    // Navigate to the equivalent URL in the chosen language. This is a full
    // page load so the BrowserRouter remounts with the correct basename and
    // the pre-rendered language-specific HTML (with proper meta tags) is
    // delivered to the user/crawler.
    const target =
      switchLangUrl(window.location.pathname, languageCode) +
      window.location.search +
      window.location.hash;
    window.location.assign(target);
  };

  const renderPrefix = (language: Language): React.ReactElement => (
    <>
      <span className="language-flag">
        <FlagSvg lang={language.code} />
      </span>
      <span className="language-code">{language.region}</span>
    </>
  );

  return (
    <div className="language-selector">
      <button
        className="language-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('nav.language') as string}
        aria-expanded={isOpen}
      >
        {renderPrefix(currentLanguage)}
        <span className="language-name">{currentLanguage.name}</span>
        <span className={`language-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div className="language-dropdown">
          {languages.map((language) => (
            <button
              key={language.code}
              className={`language-option ${language.code === currentCode ? 'active' : ''}`}
              onClick={() => handleLanguageChange(language.code)}
            >
              {renderPrefix(language)}
              <span className="language-name">{language.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
